import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, TableHead, TableRow, TableCell, TableBody, 
  Button, Badge, Spinner, AlertDialog, AlertDialogContent, 
  AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '../ui/UIComponents';
import { Plus, Edit, Trash2, ExternalLink, Database, AlertCircle, Settings, Loader2 } from 'lucide-react';
import { Application } from '../../types/rbac.types';
import { getApplications, deleteApplication } from '../../services/rbacApi';
import ApplicationForm from './ApplicationForm';

const ApplicationList: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [currentApplication, setCurrentApplication] = useState<Application | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [applicationToDelete, setApplicationToDelete] = useState<Application | null>(null);

  // Fetch applications on component mount
  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const data = await getApplications();
      setApplications(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch applications');
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (application: Application) => {
    setCurrentApplication(application);
    setIsFormOpen(true);
  };

  const handleDelete = (application: Application) => {
    setApplicationToDelete(application);
    setIsDeleteDialogOpen(true);
  };
  
  const handleNavigateToSettings = (applicationId: string) => {
    navigate(`/access-control/applications/${applicationId}/settings`);
  };

  const confirmDelete = async () => {
    if (!applicationToDelete) return;
    
    try {
      await deleteApplication(applicationToDelete.id);
      // Refresh the applications list
      fetchApplications();
      setIsDeleteDialogOpen(false);
      setApplicationToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete application');
      console.error('Error deleting application:', err);
    }
  };

  const handleFormClose = (refreshData: boolean = false) => {
    setIsFormOpen(false);
    setCurrentApplication(null);
    if (refreshData) {
      fetchApplications();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">Active</Badge>;
      case 'inactive':
        return <Badge variant="danger">Inactive</Badge>;
      case 'development':
        return <Badge variant="warning">Development</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">Applications</h2>
          <p className="text-gray-400 text-sm mt-1">Manage your platform applications</p>
        </div>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={() => setIsFormOpen(true)}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
        >
          <Plus size={16} className="mr-1.5" />
          Add Application
        </Button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg flex items-center space-x-2 animate-fadeIn">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="relative w-16 h-16">
            <div className="absolute top-0 right-0 h-16 w-16 rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute top-1 right-1 h-14 w-14 rounded-full border-4 border-t-transparent border-r-indigo-500 border-b-transparent border-l-transparent animate-spin-slow"></div>
          </div>
          <p className="text-gray-400 mt-4">Loading applications...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-b from-gray-900/30 to-gray-900/10 rounded-xl border border-gray-800/50">
          <div className="bg-blue-900/20 p-4 rounded-full border border-blue-500/20 mb-4">
            <Database className="h-10 w-10 text-blue-400/80" />
          </div>
          <h3 className="text-lg font-medium text-gray-300 mb-2">No applications found</h3>
          <p className="text-gray-400 mb-6 max-w-md text-center">Applications define the different systems that users can access through role-based permissions.</p>
          <Button 
            variant="primary" 
            onClick={() => setIsFormOpen(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
          >
            <Plus size={16} className="mr-1.5" />
            Add your first application
          </Button>
        </div>
      ) : (
        <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/30 p-0.5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto bg-background-card rounded-xl">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className="font-medium text-gray-300">Name</TableCell>
                  <TableCell className="font-medium text-gray-300">Code</TableCell>
                  <TableCell className="font-medium text-gray-300">Description</TableCell>
                  <TableCell className="font-medium text-gray-300">Status</TableCell>
                  <TableCell className="font-medium text-gray-300">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.id} className="transition-colors hover:bg-gray-800/30">
                    <TableCell className="font-medium text-blue-300">{app.name}</TableCell>
                    <TableCell><code className="bg-gray-800/50 px-2 py-0.5 rounded text-gray-300">{app.app_code}</code></TableCell>
                    <TableCell className="max-w-xs truncate text-gray-400">{app.description || '-'}</TableCell>
                    <TableCell>{getStatusBadge(app.status)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          size="xs" 
                          onClick={() => handleEdit(app)}
                          title="Edit"
                          className="border-blue-800/30 bg-blue-900/20 hover:bg-blue-800/30 text-blue-300"
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                        {app.app_url && (
                          <Button 
                            variant="outline" 
                            size="xs"
                            onClick={() => window.open(app.app_url, '_blank')}
                            title="Open application"
                            className="border-indigo-800/30 bg-indigo-900/20 hover:bg-indigo-800/30 text-indigo-300"
                          >
                            <ExternalLink size={14} className="mr-1" />
                            Open
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="xs"
                          onClick={() => handleNavigateToSettings(app.id)}
                          title="Application Settings"
                          className="border-purple-800/30 bg-purple-900/20 hover:bg-purple-800/30 text-purple-300"
                        >
                          <Settings size={14} className="mr-1" />
                          Settings
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="xs"
                          onClick={() => handleDelete(app)}
                          title="Delete"
                          className="border-red-800/30 bg-red-900/20 hover:bg-red-800/30 text-red-300"
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Application Form Modal */}
      <ApplicationForm 
        isOpen={isFormOpen} 
        onClose={handleFormClose} 
        application={currentApplication} 
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-100">Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete the application "{applicationToDelete?.name}" and all its associated roles and permissions. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-gray-300 hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ApplicationList; 